import React, { useState, useEffect, useCallback } from 'react';
import QRCodeLib from 'qrcode';
import {
  Card,
  Form,
  Select,
  Button,
  Typography,
  App,
  Spin,
  Tabs,
  Table,
  Space,
  Modal,
  Input,
  // Tag, // removed unused earlier but actually used in Agent config Descriptions, keep Descriptions
  Descriptions,
  Alert,
  Row,
  Col,
  InputNumber,
  Checkbox,
  Tooltip,
  Popconfirm,
} from 'antd';
import { 
  UserOutlined, 
  TeamOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  KeyOutlined,
  LockOutlined,
  SafetyOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { userAPI, deviceAPI, configAPI, passwordPolicyAPI, mfaAPI, apiKeyAPI, settingsAPI } from '../services/api';
import { useAuth } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
// import i18n from '../i18n'; // Not used directly, using useTranslation hook instead
import { useTranslation } from 'react-i18next';
const IS_CUSTOMER_BUILD = process.env.REACT_APP_BUILD_TARGET === 'customer';
const SUPPORT_AGENT_ROLE = IS_CUSTOMER_BUILD ? '' : ['support', 'agent'].join('_');
const SUPPORT_AGENT_LABEL_KEY = SUPPORT_AGENT_ROLE ? ['settings', 'user', 'role', SUPPORT_AGENT_ROLE].join('.') : '';

const { Title, Paragraph, Text } = Typography;

const Settings = () => {
  const { message } = App.useApp();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [userForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [systemConfigs, setSystemConfigs] = useState({});
  const [passwordPolicy, setPasswordPolicy] = useState({
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: false,
    maxAge: 90,
    preventReuse: 5,
    forceMFA: false
  });
  const [policyForm] = Form.useForm();
  // MFA 表单与状态
  const [mfaConfig, setMfaConfig] = useState(null);
  const [mfaOpsLoading, setMfaOpsLoading] = useState(false);
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpQrDataUrl, setTotpQrDataUrl] = useState(null);
  const [recoveryCount, setRecoveryCount] = useState(0);
  const [recoveryModalVisible, setRecoveryModalVisible] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [totpForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [generatedApiKeyExpiry, setGeneratedApiKeyExpiry] = useState('');
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [organizationStopCode, setOrganizationStopCode] = useState('');
  const [organizationStopCodeVisible, setOrganizationStopCodeVisible] = useState(false);
  const [organizationStopCodeRotationValue, setOrganizationStopCodeRotationValue] = useState(30);
  const [deviceStopCodeRotationValue, setDeviceStopCodeRotationValue] = useState(30);
  const [auditRetentionValue, setAuditRetentionValue] = useState(3);
  const [deviceActivityRetentionValue, setDeviceActivityRetentionValue] = useState(3);
  const [retentionOptions, setRetentionOptions] = useState([1, 3, 6, 9, 12]);
  const [stopCodeRotationOptions, setStopCodeRotationOptions] = useState([1, 5, 10, 30, 60, 120, 300, 1440, 7200, 14400, 43200]);
  const [securitySettingsLoading, setSecuritySettingsLoading] = useState(false);
  const apiEndpointUrl = `${window.location.origin}/api/docs`;
  const currentLanguage = i18n.resolvedLanguage || i18n.language || 'zh';
  const isTenantAdminRole = useCallback((role) => {
    if (IS_CUSTOMER_BUILD) {
      return false;
    }
    return role === 'tenant_admin';
  }, []);
  const canManageUsers = user?.role === 'admin' || user?.role === 'super_admin' || isTenantAdminRole(user?.role);
  const canViewUsersTab = canManageUsers || user?.role === 'auditor' || user?.role === 'user';
  const canManageTenantSecuritySettings = !IS_CUSTOMER_BUILD && (
    user?.role === 'super_admin' ||
    user?.role === 'admin' ||
    isTenantAdminRole(user?.role)
  );
  const getUserRoleLabel = useCallback((role) => {
    if (IS_CUSTOMER_BUILD) {
      const roleMap = {
        user: t('settings.user.role.user'),
        auditor: t('settings.user.role.auditor'),
        admin: t('settings.user.role.admin'),
      };
      return roleMap[role] || role;
    }
    const roleMap = {
      user: t('settings.user.role.user'),
      auditor: t('settings.user.role.auditor'),
      tenant_admin: t('settings.user.role.tenant_admin'),
      ...(SUPPORT_AGENT_ROLE ? { [SUPPORT_AGENT_ROLE]: t(SUPPORT_AGENT_LABEL_KEY) } : {}),
    };
    return roleMap[role] || role;
  }, [t]);
  const userRoleOptions = IS_CUSTOMER_BUILD
    ? [
        { label: t('settings.user.role.user'), value: 'user' },
        { label: t('settings.user.role.auditor'), value: 'auditor' },
        { label: t('settings.user.role.admin'), value: 'admin' },
      ]
    : [
        { label: t('settings.user.role.user'), value: 'user' },
        { label: t('settings.user.role.auditor'), value: 'auditor' },
        { label: t('settings.user.role.tenant_admin'), value: 'tenant_admin' },
        ...(user?.role === 'super_admin' && SUPPORT_AGENT_ROLE ? [{ label: t(SUPPORT_AGENT_LABEL_KEY), value: SUPPORT_AGENT_ROLE }] : []),
      ];
  // 定义语言辅助和 Tabs 集合
  // const isZh = i18n.language.startsWith('zh'); // Removed unused variable
  const tabItems = [];

  const loadSystemConfigs = useCallback(async () => {
    try {
      const response = await configAPI.getPublicConfigs();
      if (response.data && response.data.success) {
        // 后端返回的是键值对对象，直接使用
        setSystemConfigs(response.data.data || {});
      }
    } catch (error) {
      console.error('Failed to load system configs:', error);
    }
  }, []);

  const loadTenantSecuritySettings = useCallback(async () => {
    if (!canManageTenantSecuritySettings) {
      return;
    }
    try {
      const response = await settingsAPI.getSecurity();
      if (response?.data?.success) {
        const data = response.data.data || {};
        setOrganizationStopCode(data.organization_stop_code || '');
        setOrganizationStopCodeRotationValue(data.organization_stop_code_rotation_minutes || 30);
        setDeviceStopCodeRotationValue(data.device_stop_code_rotation_minutes || 30);
        setAuditRetentionValue(data.audit_log_retention_months || 3);
        setDeviceActivityRetentionValue(data.device_activity_retention_months || 3);
        setRetentionOptions(Array.isArray(data.retention_options) && data.retention_options.length > 0 ? data.retention_options : [1, 3, 6, 9, 12]);
        setStopCodeRotationOptions(Array.isArray(data.stop_code_rotation_options) && data.stop_code_rotation_options.length > 0 ? data.stop_code_rotation_options : [1, 5, 10, 30, 60, 120, 300, 1440, 7200, 14400, 43200]);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('settings.organization_stop_code.load_failed');
      message.error(errorMessage);
    }
  }, [canManageTenantSecuritySettings, message, t]);

  const saveTenantSecuritySettings = async () => {
    try {
      setSecuritySettingsLoading(true);
      await settingsAPI.updateSecurity({
        organization_stop_code_rotation_minutes: organizationStopCodeRotationValue,
        device_stop_code_rotation_minutes: deviceStopCodeRotationValue,
        audit_log_retention_months: auditRetentionValue,
        device_activity_retention_months: deviceActivityRetentionValue,
      });
      message.success(t('settings.retention.save_success'));
      await loadTenantSecuritySettings();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('settings.retention.save_failed');
      message.error(errorMessage);
    } finally {
      setSecuritySettingsLoading(false);
    }
  };

  const regenerateOrganizationStopCode = async () => {
    try {
      setSecuritySettingsLoading(true);
      const response = await settingsAPI.regenerateOrganizationStopCode();
      const data = response?.data?.data || {};
      setOrganizationStopCode(data.organization_stop_code || '');
      message.success(t('settings.organization_stop_code.regenerate_success'));
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('settings.organization_stop_code.regenerate_failed');
      message.error(errorMessage);
    } finally {
      setSecuritySettingsLoading(false);
    }
  };

  // 当后端返回 otpauth URL 时，生成二维码用于验证器扫描
  useEffect(() => {
    const url = totpSetup?.otpauth_url;
    if (url) {
      QRCodeLib.toDataURL(url, { width: 220, margin: 1 })
        .then((dataUrl) => setTotpQrDataUrl(dataUrl))
        .catch(() => setTotpQrDataUrl(null));
    } else {
      setTotpQrDataUrl(null);
    }
  }, [totpSetup]);

  const loadPasswordPolicy = useCallback(async () => {
    try {
      // 从后端API获取密码策略
      const response = await passwordPolicyAPI.getPasswordPolicy();
      if (response.data && response.data.success) {
        const policy = {
          minLength: response.data.data.min_length,
          requireUppercase: response.data.data.require_uppercase,
          requireLowercase: response.data.data.require_lowercase,
          requireNumbers: response.data.data.require_numbers,
          requireSpecialChars: response.data.data.require_special_chars,
          maxAge: response.data.data.max_age,
          preventReuse: response.data.data.prevent_reuse,
          forceMFA: !!response.data.data.force_mfa
        };
        setPasswordPolicy(policy);
        policyForm.setFieldsValue(policy);
      }
    } catch (error) {
      console.error('Failed to load password policy:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('settings.password_policy.load_failed', );
      message.error(errorMessage);
      // 设置默认策略
      const defaultPolicy = {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: false,
        maxAge: 90,
        preventReuse: 5,
        forceMFA: false
      };
      setPasswordPolicy(defaultPolicy);
      policyForm.setFieldsValue(defaultPolicy);
    }
  }, [policyForm, message, t]);

  const savePasswordPolicy = async (values) => {
    try {
      setLoading(true);
      // 转换字段名为后端API格式
      const policyData = {
        min_length: values.minLength,
        require_uppercase: values.requireUppercase,
        require_lowercase: values.requireLowercase,
        require_numbers: values.requireNumbers,
        require_special_chars: values.requireSpecialChars,
        max_age: values.maxAge,
        prevent_reuse: values.preventReuse,
        force_mfa: !!values.forceMFA
      };
      
      // 调用后端API保存密码策略
      const response = await passwordPolicyAPI.updatePasswordPolicy(policyData);
      if (response.data && response.data.success) {
        setPasswordPolicy(values);
        message.success(t('settings.password_policy.save_success', ));
      } else {
        throw new Error(response.data?.error || t('common.save_failed', ));
      }
    } catch (error) {
      console.error('Failed to save password policy:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '';
      message.error(errorMessage || t('settings.password_policy.save_failed', ));
    } finally {
      setLoading(false);
    }
  };

  const loadUserSettings = useCallback(async () => {
    try {
      setInitialLoading(true);
      
      // 从localStorage加载保存的设置
      const savedSettings = localStorage.getItem('userSettings');
      let settings = {
        language: currentLanguage,
        defaultGroup: '',
      };
      
      if (savedSettings) {
        settings = { ...settings, ...JSON.parse(savedSettings) };
      }
      
      try {
        const response = await userAPI.getProfile();
        if (response.data && response.data.success) {
          const userProfile = response.data.data;
          settings = {
            ...settings,
            language: userProfile.language || settings.language,
            // 确保正确映射：后端的default_device_group_id映射到前端的defaultGroup
            defaultGroup: userProfile.default_device_group_id || settings.defaultGroup,
          };
        }
      } catch (error) {
        console.log('API获取设置失败，使用本地设置:', error);
      }
      
      userForm.setFieldsValue(settings);
    } catch (error) {
      console.error('Failed to load user settings:', error);
      // 使用默认设置
      // Removed unused defaultSettings variable
      // setCurrentSettings(defaultSettings);
      // form.setFieldsValue(defaultSettings);
    } finally {
      setInitialLoading(false);
    }
  }, [currentLanguage, userForm]);

  const loadTenantUsers = useCallback(async () => {
    try {
      const response = await userAPI.getUsers();
      if (response.data && response.data.success) {
        const data = response.data.data;
        if (data && data.items) {
          setTenantUsers(Array.isArray(data.items) ? data.items : []);
        } else {
          setTenantUsers(Array.isArray(data) ? data : []);
        }
      } else {
        setTenantUsers([]);
      }
    } catch (error) {
      console.error('Failed to load tenant users:', error);
      setTenantUsers([]);
      // 不显示错误消息，静默失败
    }
  }, []);

  const loadDeviceGroups = useCallback(async () => {
    try {
      const response = await deviceAPI.getDeviceGroups();
      
      if (response.data && response.data.success) {
        // 设备组数据加载成功，但暂时不需要存储在状态中
        // 可以在这里处理设备组数据，如果将来需要的话
      }
    } catch (error) {
      console.error('Failed to load device groups:', error);
      // 不显示错误消息，静默失败
    }
  }, []);

  // 加载MFA配置与恢复码数量
  const loadMFA = useCallback(async () => {
    try {
      const [cfgResp, rcResp] = await Promise.all([
        mfaAPI.getConfig(),
        mfaAPI.getRecoveryCount()
      ]);
      if (cfgResp?.data?.success) {
        setMfaConfig(cfgResp.data.data);
      }
      if (rcResp?.data?.success) {
        setRecoveryCount(rcResp.data.data?.count || 0);
      }
    } catch (err) {
      console.warn('加载MFA配置失败:', err);
    }
  }, []);

  useEffect(() => {
    loadMFA();
  }, [loadMFA]);

  const saveMFAConfig = async () => {
    if (!mfaConfig) return;
    try {
      setMfaOpsLoading(true);
      const preferredMethod = mfaConfig.primary_method || (mfaConfig.totp_configured ? 'totp' : 'email_otp');
      const payload = {
        enabled: !!mfaConfig.enabled,
        primary_method: preferredMethod,
      };
      const resp = await mfaAPI.updateConfig(payload);
      if (resp?.data?.success) {
        message.success(t('settings.mfa.save_success', ));
      } else {
        throw new Error(resp?.data?.error || '更新失败');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      message.error(msg || t('settings.mfa.save_failed', ));
    } finally {
      setMfaOpsLoading(false);
    }
  };

  const startTOTPSetup = async () => {
    try {
      setMfaOpsLoading(true);
      const resp = await mfaAPI.startTOTPSetup();
      if (resp?.data?.success) {
        setTotpSetup(resp.data.data);
        message.info(t('settings.mfa.totp.started', ));
      } else {
        throw new Error(resp?.data?.error || '启动失败');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      message.error(msg || t('settings.mfa.totp.start_failed', ));
    } finally {
      setMfaOpsLoading(false);
    }
  };

  const verifyTOTPSetup = async () => {
    try {
      const values = await totpForm.validateFields(['code']);
      setMfaOpsLoading(true);
      // 去除空格，避免因复制/输入空格导致的校验失败
      const sanitizedCode = String(values.code || '').replace(/\s+/g, '').trim();
      const resp = await mfaAPI.verifyTOTPSetup({ code: sanitizedCode });
      if (resp?.data?.success) {
        message.success(t('settings.mfa.totp.bind_success', ));
        setTotpSetup(null);
        setMfaConfig((prev) => ({ ...(prev || {}), totp_configured: true }));
      } else {
        throw new Error(resp?.data?.error || '绑定失败');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      message.error(msg || t('settings.mfa.totp.bind_failed', ));
    } finally {
      setMfaOpsLoading(false);
    }
  };

  const requestEmailSetup = async () => {
    try {
      setMfaOpsLoading(true);
      const locale = currentLanguage;
      const resp = await mfaAPI.requestEmailSetup({ locale });
      if (resp?.data?.success) {
        message.success(t('settings.mfa.email.sent', ));
      } else {
        throw new Error(resp?.data?.error || '发送失败');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      message.error(msg || t('settings.mfa.email.send_failed', ));
    } finally {
      setMfaOpsLoading(false);
    }
  };

  const verifyEmailSetup = async () => {
    try {
      const values = await emailForm.validateFields(['code']);
      setMfaOpsLoading(true);
      const resp = await mfaAPI.verifyEmailSetup({ code: values.code });
      if (resp?.data?.success) {
        message.success(t('settings.mfa.email.bind_success', ));
        setMfaConfig((prev) => ({ ...(prev || {}), email_configured: true }));
        emailForm.resetFields();
      } else {
        throw new Error(resp?.data?.error || '绑定失败');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      message.error(msg || t('settings.mfa.email.bind_failed', ));
    } finally {
      setMfaOpsLoading(false);
    }
  };

  const generateRecoveryCodes = async () => {
    try {
      setMfaOpsLoading(true);
      const resp = await mfaAPI.generateRecoveryCodes();
      if (resp?.data?.success) {
        const codes = resp.data.data?.codes || [];
        setRecoveryCodes(codes);
        setRecoveryModalVisible(true);
        setRecoveryCount(codes.length);
      } else {
        throw new Error(resp?.data?.error || '生成失败');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      message.error(msg || t('settings.mfa.recovery.generate_failed', ));
    } finally {
      setMfaOpsLoading(false);
    }
  };

  const copyText = useCallback(async (value, successMessage, failedMessage, options = {}) => {
    const text = String(value || '').trim();
    if (!text) {
      return false;
    }
    const { silentFailure = false, silentSuccess = false } = options;

    const copyWithSelection = () => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'readonly');
      textarea.style.position = 'fixed';
      textarea.style.top = '-1000px';
      textarea.style.left = '-1000px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);
      try {
        return document.execCommand('copy');
      } catch (error) {
        return false;
      } finally {
        document.body.removeChild(textarea);
      }
    };

    try {
      let copied = false;
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(text);
          copied = true;
        } catch (error) {
          copied = false;
        }
      }
      if (!copied && window.clipboardData && typeof window.clipboardData.setData === 'function') {
        copied = window.clipboardData.setData('Text', text);
      }
      if (!copied && typeof document.execCommand === 'function') {
        copied = copyWithSelection();
      }
      if (!copied) {
        throw new Error('copy failed');
      }
      if (!silentSuccess) {
        message.success(successMessage);
      }
      return true;
    } catch (error) {
      if (!silentFailure) {
        message.error(failedMessage);
      }
      return false;
    }
  }, [message]);

  const handleGenerateApiKey = async () => {
    try {
      setApiKeyLoading(true);
      const response = await apiKeyAPI.create({});
      const payload = response?.data?.data || {};
      const nextApiKey = payload.api_key || '';
      const expiresAt = payload.key?.expires_at || '';

      if (!nextApiKey) {
        throw new Error(t('common.save_failed'));
      }

      setGeneratedApiKey(nextApiKey);
      setGeneratedApiKeyExpiry(expiresAt);
      setApiKeyVisible(false);
      await copyText(
        nextApiKey,
        t('settings.api_key.copy_success'),
        t('settings.api_key.copy_failed'),
        { silentFailure: true }
      );
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('common.save_failed');
      message.error(errorMessage);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleCopyApiKey = async () => {
    await copyText(
      generatedApiKey,
      t('settings.api_key.copy_success'),
      t('settings.api_key.copy_failed')
    );
  };

  useEffect(() => {
    loadUserSettings();
    loadSystemConfigs();
    loadTenantSecuritySettings();
    loadPasswordPolicy();
    if (canManageUsers) {
      loadTenantUsers();
      loadDeviceGroups();
    }
  }, [canManageUsers, loadUserSettings, loadSystemConfigs, loadTenantSecuritySettings, loadPasswordPolicy, loadTenantUsers, loadDeviceGroups]);

  // Removed unused onFinish function to fix eslint warning and antd Form hook warning
  // const onFinish = async (values) => {
  //   try {
  //     setLoading(true);
  //     const settingsToSave = {
  //       language: values.language,
  //       defaultGroup: values.defaultGroup,
  //     };
  //     localStorage.setItem('userSettings', JSON.stringify(settingsToSave));
  //     setCurrentSettings(settingsToSave);
  //     try {
  //       const serverData = {
  //         language: values.language,
  //         default_device_group_id: values.defaultGroup,
  //       };
  //       await userAPI.updateProfile(serverData);
  //       message.success(i18n.language.startsWith('zh') ? '设置已保存到服务器！下次打开将显示当前设置。' : 'Settings saved to server! They will be used the next time the application opens.');
  //     } catch (error) {
  //       console.log('服务器保存失败，使用本地保存:', error);
  //       message.success(i18n.language.startsWith('zh') ? '设置已保存到本地！下次打开将显示当前设置。' : 'Settings saved locally! They will be used the next time the application opens.');
  //     }
  //     if (values.language !== currentSettings.language) {
  //       const newLng = values.language.startsWith('zh') ? 'zh' : 'en';
  //       await i18n.changeLanguage(newLng);
  //       localStorage.setItem('i18nextLng', newLng);
  //       modal.confirm({
  //         title: t('settings.language.change_confirm.title'),
  //         content: t('settings.language.change_confirm.content'),
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Failed to save settings:', error);
  //     const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || (isZh ? '保存失败，请稍后重试' : 'Save failed. Please try again later');
  //     message.error(errorMessage);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleAddUser = () => {
    setEditingUser(null);
    userForm.resetFields();
    setUserModalVisible(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    userForm.setFieldsValue(user);
    setUserModalVisible(true);
  };

  const handleDeleteUser = async (record) => {
    try {
      await userAPI.deleteUser(record.id);
      message.success(t('settings.user.messages.delete_success'));
      // 重新加载用户列表
      loadTenantUsers();
    } catch (error) {
      console.error('User deletion failed:', error);
      const reason = error.response?.data?.message || error.message;
      message.error(t('settings.user.messages.delete_failed', { reason }));
    }
  };

  // 用户列表列定义
  const userColumns = [
    {
      title: t('settings.user.table.columns.username'),
      dataIndex: 'username',
      key: 'username',
      width: 180,
      render: (text, record) => record.username || '-',
    },
    {
      title: t('settings.user.table.columns.email'),
      dataIndex: 'email',
      key: 'email',
      width: 220,
      render: (text, record) => record.email || '-',
    },
    {
      title: t('settings.user.table.columns.role'),
      dataIndex: 'role',
      key: 'role',
      width: 160,
      render: (role) => getUserRoleLabel(role),
    },

    {
      title: t('settings.user.table.columns.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 200,
      render: (date) => (date ? formatDate(date) : '-'),
    },
    {
      title: t('settings.user.table.columns.actions'),
      key: 'action',
      width: 160,
      render: (_, record) => {
        const isProtectedUser = record.role === 'super_admin' || record.is_first_admin;
        return (
          <Space>
            {!isProtectedUser && (
              <Tooltip title={t('common.edit')}>
                <Button type="link" icon={<EditOutlined />} onClick={() => handleEditUser(record)} />
              </Tooltip>
            )}
            {!isProtectedUser && (
              <Popconfirm
                title={t('settings.user.modal.delete_confirm.title', { name: record.username || record.email })}
                 okText={t('common.delete')}
                 cancelText={t('common.actions.cancel')}
                 onConfirm={() => handleDeleteUser(record)}
              >
                <Tooltip title={t('common.delete')}>
                  <Button type="link" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const handleUserSubmit = async (values) => {
    try {
      if (editingUser) {
        await userAPI.updateUser(editingUser.id, values);
        message.success(t('settings.user.messages.update_success'));
      } else {
        await userAPI.createUser(values);
        message.success(t('settings.user.messages.create_success'));
      }
      setUserModalVisible(false);
      loadTenantUsers();
    } catch (error) {
      console.error('User action failed:', error);

      // 获取详细错误信息
      let errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '';

      // 根据HTTP状态码提供更友好的错误提示
      if (error.response?.status === 400) {
        message.error(
          editingUser
            ? t('settings.user.messages.update_failed', { errorMessage })
            : t('settings.user.messages.create_failed', { errorMessage })
        );
      } else if (error.response?.status === 409) {
        message.error(t('settings.user.messages.conflict'));
      } else if (error.response?.status === 403) {
        message.error(t('common.error.forbidden', ));
      } else if (error.response?.status === 401) {
        message.error(t('common.error.unauthorized', ));
      } else if (error.response?.status >= 500) {
        message.error(t('common.error.server', ));
      } else {
        message.error(
          editingUser
            ? t('settings.user.messages.update_failed', { errorMessage })
            : t('settings.user.messages.create_failed', { errorMessage })
        );
      }
    }
  };

  // const userColumnsLegacy = [
  //   {
  //     title: '用户名',
  //     dataIndex: 'username',
  //     key: 'username',
  //     render: (username, record) => {
  //       // 如果没有username，显示first_name + last_name
  //       if (!username && (record.first_name || record.last_name)) {
  //         return `${record.first_name || ''} ${record.last_name || ''}`.trim();
  //       }
  //       return username || record.email;
  //     },
  //   },
  //   {
  //     title: '邮箱',
  //     dataIndex: 'email',
  //     key: 'email',
  //   },
  //   {
  //     title: '角色',
  //     dataIndex: 'role',
  //     key: 'role',
  //     render: (role) => {
  //       const roleMap = {
  //         'tenant_admin': { color: 'red', text: '组织管理员' },
  //         'user': { color: 'blue', text: '普通用户' },
  //         'auditor': { color: 'green', text: '审计员' },
  //       };
  //       const config = roleMap[role] || { color: 'default', text: role };
  //       return <Tag color={config.color}>{config.text}</Tag>;
  //     },
  //   },
  //   {
  //     title: '状态',
  //     dataIndex: 'status',
  //     key: 'status',
  //     render: (status) => (
  //       <Tag color={status === 'active' ? 'green' : 'red'}>
  //         {status === 'active' ? '活跃' : '禁用'}
  //       </Tag>
  //     ),
  //   },
  //   {
  //     title: '创建时间',
  //     dataIndex: 'created_at',
  //     key: 'created_at',
  //     render: (text) => text ? formatDate(text) : '-',
  //   },
  //   {
  //     title: '操作',
  //     key: 'action',
  //     render: (_, record) => {
  //       // 检查是否为超级管理员或组织的第一个管理员
  //       const isProtectedUser = record.role === 'super_admin' || 
  //         (record.role === 'tenant_admin' && record.is_first_admin);
  //       
  //       // 检查当前用户是否有管理权限
  //       const hasManagePermission = user?.role === 'tenant_admin' || user?.role === 'super_admin';
  //       
  //       if (!hasManagePermission) {
  //         return (
  //           <Tag color="blue">仅可查看</Tag>
  //         );
  //       }
  //       
  //       return (
  //         <Space size="middle">
  //           <Button
  //             type="link"
  //             icon={<EditOutlined />}
  //             onClick={() => handleEditUser(record)}
  //           >
  //             编辑
  //           </Button>
  //           {!isProtectedUser && (
  //             <Button
  //               type="link"
  //               danger
  //               icon={<DeleteOutlined />}
  //               onClick={() => {
  //                 modal.confirm({
  //                   title: '确认删除',
  //                   content: `确定要删除用户 "${record.username || record.email}" 吗？`,
  //                   onOk: () => handleDeleteUser(record.id),
  //                 });
  //               }}
  //             >
  //               删除
  //             </Button>
  //           )}
  //         </Space>
  //       );
  //     },
  //   },
  // ];

  if (initialLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('settings.loading')}</div>
      </div>
    );
  }

  const recommendAuthenticatorText = i18n.language?.startsWith('zh')
    ? '建议优先使用 Authenticator 作为 MFA 主方式。它比邮箱验证码更稳定，也更适合组织强制 MFA 的场景。'
    : 'Authenticator is recommended as the primary MFA method. It is more reliable than email codes and is better suited for enforced MFA.';
  const emailFallbackText = i18n.language?.startsWith('zh')
    ? '当前可以先用邮箱验证码完成验证。完成后建议继续绑定 Authenticator，并将其设置为主方式。'
    : 'You can use email verification as a temporary fallback. After verification, bind an Authenticator app and set it as your primary method.';

  // 组织管理员、超级管理员、审计员和普通用户可以看到用户管理
  if (canViewUsersTab) {
    const hasManagePermission = canManageUsers;
    tabItems.push({
      key: 'users',
      label: <span><UserOutlined />{t('settings.user.tab_title')}</span>,
      children: (
        <Card>
          <Title level={4}>{t('settings.user.title')}</Title>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              {hasManagePermission && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddUser} align="right">
                  {t('settings.user.actions.add')}
                </Button>
              )}
            </div>
          </div>
          <Table
            columns={hasManagePermission ? userColumns : userColumns.filter(col => col.key !== 'action')}
            dataSource={tenantUsers}
            rowKey="id"
            pagination={{
              total: tenantUsers.length,
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => t('device.pagination.show_total', { start: range[0], end: range[1], total }),
            }}
            locale={{
              emptyText: t('settings.user.table.empty'),
            }}
          />
        </Card>
      ),
    });

    // 密码策略管理（仅管理员可见）
    if (hasManagePermission) {
      tabItems.push({
        key: 'password-policy',
        label: <span><LockOutlined />{t('settings.password_policy.tab_title')}</span>,
        children: (
          <Card>
            <Title level={4}>{t('settings.password_policy.title')}</Title>
            <Form form={policyForm} layout="vertical" onFinish={savePasswordPolicy} initialValues={passwordPolicy}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label={t('settings.password_policy.fields.min_length.label', )} name="minLength" tooltip={t('settings.password_policy.fields.min_length.tooltip')}>
                    <InputNumber min={6} max={32} placeholder="6" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={t('settings.password_policy.fields.expiration_days.label', )} name="maxAge" tooltip={t('settings.password_policy.fields.expiration_days.tooltip')}>
                    <InputNumber min={0} max={365} placeholder={t('settings.password_policy.fields.expiration_days.placeholder')} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="requireUppercase" valuePropName="checked">
                    <Checkbox>{t('settings.password_policy.fields.require_uppercase')}</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="requireLowercase" valuePropName="checked">
                    <Checkbox>{t('settings.password_policy.fields.require_lowercase')}</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="requireNumbers" valuePropName="checked">
                    <Checkbox>{t('settings.password_policy.fields.require_digit')}</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="requireSpecialChars" valuePropName="checked">
                    <Checkbox>{t('settings.password_policy.fields.require_special')}</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="forceMFA" valuePropName="checked" tooltip={t('settings.password_policy.fields.force_mfa.tooltip', )}>
                    <Checkbox>{t('settings.password_policy.fields.force_mfa.label', )}</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={t('settings.password_policy.fields.password_history_count.label', )} name="preventReuse" tooltip={t('settings.password_policy.fields.password_history_count.tooltip')}>
                <InputNumber min={0} max={10} placeholder={t('settings.password_policy.fields.password_history_count.placeholder')} style={{ width: '100%' }} />
              </Form.Item>
              <Alert
                message={t('settings.password_policy.fields.note.title')}
                description={t('settings.password_policy.fields.note.description')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form.Item>
                <Button type="primary" htmlType="submit" icon={<LockOutlined />} loading={loading}>
                  {t('settings.password_policy.save')}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        ),
      });

      if (canManageTenantSecuritySettings) {
        tabItems.push({
          key: 'organization-stop-code',
          label: <span><KeyOutlined />{t('settings.organization_stop_code.tab_title')}</span>,
          children: (
            <Card>
              <Title level={4}>{t('settings.organization_stop_code.title')}</Title>
              <Paragraph type="secondary">{t('settings.organization_stop_code.description')}</Paragraph>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Text strong>{t('settings.organization_stop_code.current_label')}</Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input.Password
                    value={organizationStopCode}
                    readOnly
                    visibilityToggle={{
                      visible: organizationStopCodeVisible,
                      onVisibleChange: setOrganizationStopCodeVisible,
                    }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyText(organizationStopCode, t('settings.organization_stop_code.copy_success'), t('settings.organization_stop_code.copy_failed'))}
                  >
                    {t('settings.organization_stop_code.copy')}
                  </Button>
                </Space.Compact>
                <Button type="primary" icon={<KeyOutlined />} loading={securitySettingsLoading} onClick={regenerateOrganizationStopCode}>
                  {t('settings.organization_stop_code.regenerate')}
                </Button>
              </Space>
            </Card>
          ),
        });

        tabItems.push({
          key: 'log-retention',
          label: <span><FileTextOutlined />{t('settings.retention.tab_title')}</span>,
          children: (
            <Card>
              <Title level={4}>{t('settings.retention.title')}</Title>
              <Paragraph type="secondary">{t('settings.retention.description')}</Paragraph>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap align="center">
                  <Text strong>{t('settings.retention.organization_stop_code_rotation_label')}</Text>
                  <Select
                    value={organizationStopCodeRotationValue}
                    style={{ width: 220 }}
                    options={stopCodeRotationOptions.map((value) => ({
                      value,
                      label: t(`settings.retention.rotation_minutes.${value}`),
                    }))}
                    onChange={setOrganizationStopCodeRotationValue}
                  />
                </Space>
                <Space wrap align="center">
                  <Text strong>{t('settings.retention.device_stop_code_rotation_label')}</Text>
                  <Select
                    value={deviceStopCodeRotationValue}
                    style={{ width: 220 }}
                    options={stopCodeRotationOptions.map((value) => ({
                      value,
                      label: t(`settings.retention.rotation_minutes.${value}`),
                    }))}
                    onChange={setDeviceStopCodeRotationValue}
                  />
                </Space>
                <Space wrap align="center">
                  <Text strong>{t('settings.retention.audit_log_label')}</Text>
                  <Select
                    value={auditRetentionValue}
                    style={{ width: 220 }}
                    options={retentionOptions.map((value) => ({
                      value,
                      label: t(`settings.retention.months.${value}`),
                    }))}
                    onChange={setAuditRetentionValue}
                  />
                </Space>
                <Space wrap align="center">
                  <Text strong>{t('settings.retention.device_activity_label')}</Text>
                  <Select
                    value={deviceActivityRetentionValue}
                    style={{ width: 220 }}
                    options={retentionOptions.map((value) => ({
                      value,
                      label: t(`settings.retention.months.${value}`),
                    }))}
                    onChange={setDeviceActivityRetentionValue}
                  />
                </Space>
                <Button type="primary" icon={<FileTextOutlined />} loading={securitySettingsLoading} onClick={saveTenantSecuritySettings}>
                  {t('settings.retention.save')}
                </Button>
              </Space>
            </Card>
          ),
        });
      }
    }

    tabItems.push({
      key: 'agent-config',
      label: <span><TeamOutlined />{t('settings.agent_config.tab_title')}</span>,
      children: (
        <Card>
          <Title level={4}>{t('settings.agent_config.title')}</Title>
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('settings.agent_config.fields.server_address.label')}>
              <Text code>{systemConfigs.server_url || window.location.origin}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.agent_config.fields.tenant_id.label')}>
              <Text code>{user?.tenant_id || process.env.REACT_APP_DEFAULT_TENANT_ID || '1'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.agent_config.fields.tenant_code.label')}>
              <Text code>{user?.tenant_code || process.env.REACT_APP_DEFAULT_TENANT_CODE || 'default'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.agent_config.fields.tenant_name.label')}>
              {user?.tenant_name || process.env.REACT_APP_DEFAULT_TENANT_NAME || t('settings.agent_config.default_tenant_name', )}
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.agent_config.fields.api_endpoint.label')}>
              <Typography.Link href={apiEndpointUrl} target="_blank" rel="noreferrer">
                {apiEndpointUrl}
              </Typography.Link>
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 24 }}>
            <Title level={5}>{t('settings.api_key.title')}</Title>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Text type="secondary">{t('settings.api_key.hint')}</Text>
              <Button
                type="primary"
                icon={<KeyOutlined />}
                onClick={handleGenerateApiKey}
                loading={apiKeyLoading}
              >
                {t('settings.api_key.generate')}
              </Button>
              {generatedApiKey && (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong>{t('settings.api_key.value_label')}</Text>
                  <Space.Compact style={{ width: '100%' }}>
                    <Input.Password
                      value={generatedApiKey}
                      readOnly
                      visibilityToggle={{
                        visible: apiKeyVisible,
                        onVisibleChange: setApiKeyVisible,
                      }}
                    />
                    <Button icon={<CopyOutlined />} onClick={handleCopyApiKey}>
                      {t('settings.api_key.copy')}
                    </Button>
                  </Space.Compact>
                  {generatedApiKeyExpiry && (
                    <Text type="secondary">
                      {t('settings.api_key.expires_at')}: {formatDate(generatedApiKeyExpiry)}
                    </Text>
                  )}
                </Space>
              )}
            </Space>
          </div>
        </Card>
      ),
    });

    // MFA 设置页
    tabItems.push({
      key: 'mfa',
      label: <span><SafetyOutlined />{t('settings.mfa.tab_title', )}</span>,
      children: (
        <Card loading={initialLoading}>
          <Title level={4}>{t('settings.mfa.title', )}</Title>

          {/* 配置管理 */}
          <Title level={5}>{t('settings.mfa.config.title', )}</Title>
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            {!mfaConfig?.totp_configured && (
              <Alert type="info" showIcon message={recommendAuthenticatorText} />
            )}
            {mfaConfig?.email_configured && !mfaConfig?.totp_configured && (
              <Alert type="warning" showIcon message={emailFallbackText} />
            )}
            <Checkbox
              checked={!!mfaConfig?.enabled}
              onChange={(e) => setMfaConfig((prev) => ({ ...(prev || {}), enabled: e.target.checked }))}
            >
              {t('settings.mfa.config.enabled', )}
            </Checkbox>
            <Form layout="inline">
              <Form.Item label={t('settings.mfa.config.primary', )}>
                <Select
                  value={mfaConfig?.primary_method || (mfaConfig?.totp_configured ? 'totp' : 'email_otp')}
                  onChange={(val) => setMfaConfig((prev) => ({ ...(prev || {}), primary_method: val }))}
                  style={{ width: 240 }}
                >
                  <Select.Option value="email_otp" disabled={!mfaConfig?.email_configured}>
                    {t('settings.mfa.methods.email_otp', )}
                  </Select.Option>
                  <Select.Option value="totp" disabled={!mfaConfig?.totp_configured}>
                    {t('settings.mfa.methods.totp', )}
                  </Select.Option>
                </Select>
              </Form.Item>
              <Form.Item>
                <Button type="primary" onClick={saveMFAConfig} loading={mfaOpsLoading} icon={<LockOutlined />}>
                  {t('common.save', )}
                </Button>
              </Form.Item>
            </Form>
          </Space>

          {/* TOTP 绑定 */}
          <Title level={5} style={{ marginTop: 16 }}>{t('settings.mfa.totp.title', )}</Title>
          {!mfaConfig?.totp_configured ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {!totpSetup ? (
                <Button onClick={startTOTPSetup} loading={mfaOpsLoading}>
                  {t('settings.mfa.totp.start', )}
                </Button>
              ) : (
                <>
                  <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                    message={t('settings.mfa.totp.hint', )}
                  />
                  <Descriptions size="small" bordered column={1}>
                    <Descriptions.Item label={t('settings.mfa.totp.secret', )}>{totpSetup?.secret}</Descriptions.Item>
                    <Descriptions.Item label={t('settings.mfa.totp.otpauth_url', )}>{totpSetup?.otpauth_url}</Descriptions.Item>
                  </Descriptions>
                  {totpQrDataUrl && (
                    <div style={{ marginTop: 12 }}>
                      <Text strong>{t('settings.mfa.totp.qr_title', )}</Text>
                      <div style={{ marginTop: 8 }}>
                        <img src={totpQrDataUrl} alt={t('settings.mfa.totp.qr_alt', )} style={{ width: 220, height: 220 }} />
                      </div>
                      <Paragraph type="secondary" style={{ marginTop: 8 }}>
                        {t('settings.mfa.totp.qr_helper', )}
                      </Paragraph>
                    </div>
                  )}
                  <Form form={totpForm} layout="inline" style={{ marginTop: 12 }}>
                    <Form.Item name="code" label={t('settings.mfa.form.code.label', )}
                      rules={[{ required: true, message: t('settings.mfa.form.code.required', ) }]}
                    >
                      <Input placeholder={t('settings.mfa.form.code.placeholder', )} style={{ width: 240 }} />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" onClick={verifyTOTPSetup} loading={mfaOpsLoading}>
                        {t('settings.mfa.totp.verify', )}
                      </Button>
                    </Form.Item>
                  </Form>
                </>
              )}
            </Space>
          ) : (
            <Alert type="success" showIcon message={t('settings.mfa.totp.bound', )} />
          )}

          {/* 邮箱绑定 */}
          <Title level={5} style={{ marginTop: 16 }}>{t('settings.mfa.email.title', )}</Title>
          {mfaConfig?.email_configured ? (
            <Alert type="success" showIcon message={t('settings.mfa.email.bound', )} />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button onClick={requestEmailSetup} loading={mfaOpsLoading}>
                {t('settings.mfa.email.send_code', )}
              </Button>
              <Form form={emailForm} layout="inline">
                <Form.Item name="code" label={t('settings.mfa.form.code.label', )}
                  rules={[{ required: true, message: t('settings.mfa.form.code.required', ) }]}
                >
                  <Input placeholder={t('settings.mfa.form.code.email_placeholder', )} style={{ width: 240 }} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" onClick={verifyEmailSetup} loading={mfaOpsLoading}>
                    {t('settings.mfa.email.verify', )}
                  </Button>
                </Form.Item>
              </Form>
            </Space>
          )}

          {/* 恢复码管理 */}
          <Title level={5} style={{ marginTop: 16 }}>{t('settings.mfa.recovery.title', )}</Title>
          <Space>
            <Typography.Text>{t('settings.mfa.recovery.count', { count: recoveryCount })}</Typography.Text>
            <Button onClick={generateRecoveryCodes} loading={mfaOpsLoading}>
              {t('settings.mfa.recovery.generate', )}
            </Button>
          </Space>

          <Modal
            title={t('settings.mfa.recovery.modal.title', )}
            open={recoveryModalVisible}
            onCancel={() => setRecoveryModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setRecoveryModalVisible(false)}>{t('common.close', )}</Button>
            ]}
          >
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={t('settings.mfa.recovery.modal.hint', )}
            />
            <Space direction="vertical" style={{ width: '100%' }}>
              {recoveryCodes.map((code, idx) => (
                <Input key={idx} value={code} readOnly />
              ))}
            </Space>
          </Modal>
        </Card>
      ),
    });
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 8 }}>{t('settings.page.title')}</Title>

      <Tabs items={tabItems} />

      {/* 用户编辑模态框 */}
      <Modal
        title={editingUser ? t('settings.user.modal.edit_title') : t('settings.user.modal.create_title')}
        open={userModalVisible}
        onCancel={() => setUserModalVisible(false)}
        footer={null}
      >
        <Form form={userForm} layout="vertical" onFinish={handleUserSubmit}>
          <Form.Item name="username" label={t('settings.user.form.username.label')} rules={[{ required: true, message: t('settings.user.form.username.required') }]}>
            <Input placeholder={t('settings.user.form.username.placeholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="first_name" label={t('settings.user.form.last_name.label')} rules={[{ required: false, message: t('settings.user.form.last_name.required') }]}>
                <Input placeholder={t('settings.user.form.last_name.placeholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label={t('settings.user.form.first_name.label')} rules={[{ required: false, message: t('settings.user.form.first_name.required') }]}>
                <Input placeholder={t('settings.user.form.first_name.placeholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="email" label={t('settings.user.form.email.label')} rules={[{ required: true, message: t('settings.user.form.email.required') }, { type: 'email', message: t('settings.user.form.email.invalid') }]}>
            <Input placeholder={t('settings.user.form.email.placeholder')} />
          </Form.Item>

          {!editingUser && (
            <Form.Item name="password" label={t('settings.user.form.password.label')} rules={[{ required: true, message: t('settings.user.form.password.required') }, { min: 6, message: t('settings.user.form.password.min_length') }]}>
              <Input.Password placeholder={t('settings.user.form.password.placeholder')} />
            </Form.Item>
          )}

          <Form.Item name="role" label={t('settings.user.form.role.label')} rules={[{ required: true, message: t('settings.user.form.role.required') }]}>
            <Select options={userRoleOptions} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setUserModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {editingUser ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default Settings;
